import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appSettings } from '../../../environments/app-settings';
import { EntityAction } from '../../models/entity-action';
import { PageResult } from '../../models/page-result';
import { TableConfiguration } from '../../models/table-configuration';
import { VirtualEnvironment } from '../models/virtual-environment';
import { VirtualEnvironmentVersion } from '../models/virtual-environment-version';
import { WorkItem } from '../models/work-item';

/**
 * Encapsulates virtual environment request related operations.
 */
@Injectable()
export class VirtualEnvironmentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${appSettings.api.url}ui/virtualenvironments`;

  /**
   * Retrieves page of virtual environment requests.
   *
   * @param sortBy Name of the field for ordering.
   * @param isDescending Flag which displays if ordering should be descending.
   * @param searchText Search text.
   * @param filters Array of statuses to display.
   * @param continuationToken Next page token.
   * @returns A page of virtual environment requests.
   */
  public getPage(sortBy: string, isDescending: boolean, searchText: string, filters: string[], continuationToken: string): Observable<PageResult<WorkItem>> {
    let params = new HttpParams()
      .set('sortBy', sortBy)
      .set('isDescending', isDescending.toString());

    if (searchText)
      params = params.set('searchText', searchText);

    for (const filter of filters)
      params = params.append('filter', filter);

    let headers = new HttpHeaders();
    if (continuationToken)
      headers = headers.set('x-syk-continuation', continuationToken);

    return this.http.get<PageResult<WorkItem>>(this.baseUrl, { params, headers });
  }

  /**
   * Retrieves blank virtual machine request with allowed actions list.
   *
   * @returns Blank virtual machine request.
   */
  public blank(): Observable<EntityAction<VirtualEnvironment>> {
    return this.http.get<EntityAction<VirtualEnvironment>>(`${this.baseUrl}/blank`);
  }

  /**
   * Retrieves existing virtual machine request with allowed actions list.
   *
   * @param id Virtual Machine request ID.
   * @param creatorID Virtual Machine request creator user ID.
   * @returns Virtual Machine request.
   */
  public get(id: string, creatorID: string): Observable<EntityAction<VirtualEnvironment>> {
    return this.http.get<EntityAction<VirtualEnvironment>>(`${this.baseUrl}/${id}/users/${creatorID}`);
  }

  /**
   * Retrieves existing virtual environment request version with allowed actions list.
   *
   * @param id Virtual Environment Form ID.
   * @param creatorID Virtual Environment  creator user ID.
   * @param version Virtual Environment version number.
   * @returns Virtual Environment request version.
   */
  public getVersion(id: string, creatorID: string, version: number): Observable<EntityAction<VirtualEnvironmentVersion>> {
    return this.http.get<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${id}/versions/${version}/users/${creatorID}`);
  }

  /**
   * Adds new virtual environment request.
   *
   * @param version Virtual Environment request to add.
   * @returns Actual virtual environment request.
   */
  public create(version: VirtualEnvironmentVersion): Observable<EntityAction<VirtualEnvironment>> {
    return this.http.post<EntityAction<VirtualEnvironment>>(this.baseUrl, version);
  }

  /**
   * Updates existing virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @returns Actual data virtual environment request version.
  */
  public update(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion): Observable<EntityAction<VirtualEnvironmentVersion>> {
    return this.http.put<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}`, version);
  }

  /**
   * Deletes virtual environment record.
   *
   * @param virtualEnv Virtual Environment to delete.
   * @returns Removes virtual environment request record.
  */
  public delete(virtualEnv: VirtualEnvironment): Observable<any> {
    return this.http.delete<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/users/${virtualEnv.creatorID}/delete`);
  }

  /**
   * Rejects virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @param reason Rejection reason.
   * @returns Actual data virtual environment request version.
  */
  public reject(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion, reason: string): Observable<EntityAction<VirtualEnvironmentVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.put<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}/reject`, null, { params });
  }

  /**
   * Cancels virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @param reason Cancellation reason.
   * @returns Actual data virtual environment request version.
   */
  public cancel(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion, reason: string): Observable<EntityAction<VirtualEnvironmentVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.delete<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}`, { params });
  }

  /**
   * Creates updated virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @param reason Update reason.
   * @returns Updated virtual environment request version..
   */
  public setUpdating(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion, reason: string): Observable<EntityAction<VirtualEnvironmentVersion>> {
    const params = new HttpParams()
      .set('reason', reason);

    return this.http.post<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}`, null, { params });
  }

  /**
   * Submits virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @returns Actual virtual environment request version.
   */
  public submit(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion): Observable<EntityAction<VirtualEnvironmentVersion>> {
    return this.http.put<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}/submit`, null);
  }

  /**
   * Approves virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @returns Actual data virtual environment version.
   */
  public approve(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion): Observable<EntityAction<VirtualEnvironmentVersion>> {
    return this.http.put<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}/approve`, null);
  }

  /**
   * Completes virtual environment request version.
   *
   * @param virtualEnv Virtual Environment request.
   * @param version Virtual Environment request version to update.
   * @returns Actual completed data virtual environment version.
  */
  public complete(virtualEnv: VirtualEnvironment, version: VirtualEnvironmentVersion): Observable<EntityAction<VirtualEnvironmentVersion>> {
    return this.http.put<EntityAction<VirtualEnvironmentVersion>>(`${this.baseUrl}/${virtualEnv.id}/versions/${version.version}/users/${virtualEnv.creatorID}/complete`, null);
  }

  /**
   * Copy new virtual environment request.
   *
   * @param virtualEnv Virtual environment to copy.
   * @returns Actual virtual environment request from last version.
   */
  public copy(virtualEnv: VirtualEnvironment): Observable<EntityAction<VirtualEnvironment>> {
    return this.http.get<EntityAction<VirtualEnvironment>>(`${this.baseUrl}/${virtualEnv.id}/users/${virtualEnv.creatorID}/copy`);
  }

  /**
   * Retrieves configuration data for virtual environment table with allowed actions list.
   *
   * @returns EntityAction instance with TableConfiguration.
   */
  public getConfiguration(): Observable<EntityAction<TableConfiguration>> {
    return this.http.get<EntityAction<TableConfiguration>>(`${this.baseUrl}/configuration`);
  }
}
